<template>
  <div class="markers-intention" v-if="!refreshingData">
    <transition-group name="fade">
      <gmap-custom-marker
        :marker="location._geoloc"
        :ref="`location-${location.id}`"
        class="markers-intention__marker"
        v-for="(location, idx) in locations"
        :key="`location-${idx}`"
        :zIndex="
          currentLocation === location || ($route.name === 'location' && location.brand_status)
            ? 52
            : location.first
            ? 51
            : 50
        "
        @click.native.stop="toggleElements(location)"
      >
        <div
          class="markers-intention__marker__content"
          :class="{
            'markers-intention__marker__content--hightlight':
              currentLocation === location ||
              settings.marker.score === true ||
              ($route.name === 'location' && location.internal_id === $route.params.location),
            'markers-intention__marker__content--tiny': settings.marker.tiny === true,
          }"
        >
          <marker-map-intention
            class="markers-intention__marker__content__img"
            :filters="filters"
            :lge-active="location.lge_status"
            :source="location.source"
          />
        </div>
      </gmap-custom-marker>
    </transition-group>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import GmapCustomMarker from 'vue2-gmap-custom-marker'
import MarkerMapIntention from '@/components/Marker/MapIntention.vue'
import { getValueBySource } from '@/utils/extractor.util'
import { launchGAEvent } from '@/utils/gtm.util'
import { buildAlgoliaFacets } from '@/utils/algolia.util'

export default {
  name: 'MarkersIntention',
  components: {
    GmapCustomMarker,
    MarkerMapIntention,
  },
  props: {
    map: {
      type: Object,
      required: true,
    },
    locations: {
      type: Array,
      required: true,
    },
    filters: {
      type: Array,
      required: true,
    },
    activeFilters: {
      type: Array,
      required: true,
    },
    currentSettings: {
      type: Object,
      required: true,
    },
  },
  watch: {
    async mapBounds() {
      const bounds = this.map.getBounds()
      const mapCenter = this.map.getCenter()
      const northEst = bounds.getNorthEast()
      const southWest = bounds.getSouthWest()
      const box = [[northEst.lat(), northEst.lng(), southWest.lat(), southWest.lng()]]
      // eslint-disable-next-line no-undef
      const mapRadius = Math.round(google.maps.geometry.spherical.computeDistanceBetween(mapCenter, northEst))
      const mapLatLng = `${mapCenter.lat()},${mapCenter.lng()}`
      this.setCurrentMapCenter(mapLatLng)
      this.setCurrentMapRadius(mapRadius)

      await this.searchMultipleQueries([
        {
          indexName: this.markersIndex.indexName,
          commitName: 'data/SET_LOCATIONS',
          saveSettings: true,
          commitSettings: 'algolia/UPDATE_SETTINGS_MARKER',
          params: {
            length: this.nbDisplayMarker,
            offset: 0,
            insideBoundingBox: box,
            ...buildAlgoliaFacets(
              [
                {
                  key: 'source',
                  values: this.activeFilters,
                },
              ],
              true
            ),
          },
        },
        {
          indexName: this.indexCircleOrCountiesByZoom,
          commitName: 'algolia/SET_SOURCES',
          params: {
            distinct: 1,
            insideBoundingBox: box,
            ...buildAlgoliaFacets([
              {
                key: 'category',
                values: [],
              },
            ]),
          },
        },
        {
          indexName: this.indexCircleOrCountiesByZoom,
          commitName: 'algolia/SET_CIRCLES_IN_BOX',
          getAllHits: true,
          positionInRequest: 3,
          params: {
            hitsPerPage: this.nbDisplayCircle,
            insideBoundingBox: box,
            ...buildAlgoliaFacets([
              {
                key: 'category',
                values: [],
              },
            ]),
          },
        },
      ])
    },
  },
  data() {
    return {
      currentValue: getValueBySource,
    }
  },
  mounted() {
    this.toggleFilters(false)
    this.toggleLegends(true)
    this.$emit('setZoomDefault', this.map.zoom)
    this.map.setOptions({ minZoom: 7 })
  },
  computed: {
    ...mapState({
      markersIndex: state => state.algolia.markersIndex,
      circlesIndex: state => state.algolia.circlesIndex,
      countiesIndex: state => state.algolia.countiesIndex,
      nbDisplayCircle: state => state.algolia.settings.circle.nbMaxDisplay,
      nbDisplayMarker: state => state.algolia.settings.marker.nbMaxDisplay,

      currentLocation: state => state.data.currentLocation,
      circlesZone: state => state.data.circlesZone,
      settings: state => state.radar.settings,
      mapBounds: state => state.radar.map.bounds,
      refreshingData: state => state.radar.refreshing.data,
      currentZoom: state => state.radar.currentZoom,

      currentSource: state => state.refine.currentSource,
    }),
    ...mapGetters({
      indexCircleOrCountiesByZoom: 'algolia/indexCircleOrCountiesByZoom',
    }),
  },
  methods: {
    ...mapActions({
      searchMultipleQueries: 'algolia/searchMultipleQueries',

      setCurrentLocation: 'data/setCurrentLocation',

      toggleFilters: 'radar/toggleFilters',
      toggleLegends: 'radar/toggleLegends',
      setCurrentMapCenter: 'radar/setCurrentMapCenter',
      setCurrentMapRadius: 'radar/setCurrentMapRadius',
      toggleSearch: 'radar/toggleSearch',
      toggleCompare: 'radar/toggleCompare',
    }),
    toggleElements(location) {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_details`, location.name)
      this.toggleSearch(false)
      this.toggleCompare(false)
      this.setCurrentLocation(location)
    },
  },
  beforeDestroy() {
    this.setCurrentLocation(null)
  },
}
</script>

<style lang="scss" scoped>
.markers-intention {
  &__marker {
    cursor: pointer;
    &__content {
      display: flex;
      position: relative;
      transform-style: preserve-3d;
      transition: transform $transition-duration-fast $transition-effect-default;
      width: 24px;
      &--tiny {
        transform: scale(0.5) translateY(10px);
      }
      &--hightlight {
        transform: scale(2) translateY(-5px);
      }
      &__img {
        display: inline-flex;
      }
    }
  }
}
</style>
